// General API service helper with automatic token refresh
import authService from './authService'

// Get base API path from environment
const getBasePath = () => {
  return import.meta.env.VITL_API_BASE_URL || '/api'
}

// Enhanced API call helper with automatic token management
export const apiCall = async (relativePath: string, options: RequestInit = {}) => {
  const basePath = getBasePath()
  // Ensure there's always a slash between basePath and relativePath
  const separator = relativePath.startsWith('/') ? '' : '/'
  const url = `${basePath}${separator}${relativePath}`
  
  try {
    // Get a valid token (will refresh if needed)
    const token = await authService.getValidAccessToken()
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    })

    // Handle 401 Unauthorized - token might have expired between check and use
    if (response.status === 401) {
      console.log('Got 401, attempting to refresh token and retry...')
      
      const refreshed = await authService.handle401()
      if (refreshed) {
        // Retry the request with new token
        const newToken = await authService.getValidAccessToken()
        const retryResponse = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': 'application/json',
            ...options.headers,
          },
          ...options,
        })
        
        if (!retryResponse.ok) {
          throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText}`)
        }
        
        try {
          return await retryResponse.json()
        } catch {
          return null
        }
      } else {
        // Refresh failed, redirect to login
        authService.logout()
        window.location.href = '/'
        throw new Error('Session expired. Please log in again.')
      }
    }

    if (!response.ok) {
      // Try to get error details from response body
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`
      try {
        const errorData = await response.json()
        if (errorData.message) {
          errorMessage += ` - ${errorData.message}`
        }
      } catch {
        // Ignore JSON parsing errors for error responses
      }
      throw new Error(errorMessage)
    }

    // Always try to parse JSON for successful responses
    // Some methods like DELETE might not return JSON, so handle gracefully
    try {
      return await response.json()
    } catch {
      // Return null if response body is empty or not JSON (common for DELETE, some PUT/PATCH)
      return null
    }
  } catch (error) {
    // Check if it's an auth error
    if (error instanceof Error && error.message.includes('authentication')) {
      // Redirect to login
      window.location.href = '/'
    }
    
    // Re-throw our custom errors or network errors
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Network error or unexpected failure')
  }
}